import type { StripConfig as StripState } from "../../project/schema";
import { useMeters, useProject, useServices } from "../context";
import { Panel, Slider, Toggle, ms, panText, pct } from "./controls";

export function MixerPanel() {
  const { controller } = useServices();
  const tracks = useProject((p) => p.tracks);
  const master = useProject((p) => p.master);
  const fx = useProject((p) => p.effects);
  const metronome = useProject((p) => p.metronome);
  const meters = useMeters();

  const setStrip = (id: string, patch: Partial<StripState>) => controller.updateProject((p) => ({ ...p, tracks: p.tracks.map((t) => (t.id === id ? { ...t, mixer: { ...t.mixer, ...patch } } : t)) }));
  const setFx = <K extends keyof typeof fx>(k: K, patch: Partial<(typeof fx)[K]>) => controller.updateProject((p) => ({ ...p, effects: { ...p.effects, [k]: { ...p.effects[k], ...patch } } }));

  return (
    <Panel title="Mixer" className="span-all">
      <div className="mixer">
        {tracks.map((t) => (
          <div className="strip" key={t.id} role="group" aria-label={`${t.name} channel`}>
            <h3>{t.name}</h3>
            <LevelMeter label={`${t.name} level`} value={meters.tracks[t.id] ?? 0} />
            <div className="strip-buttons">
              <Toggle pressed={t.mixer.mute} onChange={(mute) => setStrip(t.id, { mute })} label={`Mute ${t.name}`} className="mute">M</Toggle>
              <Toggle pressed={t.mixer.solo} onChange={(solo) => setStrip(t.id, { solo })} label={`Solo ${t.name}`} className="solo">S</Toggle>
            </div>
            <Slider label="Volume" value={t.mixer.volume} min={0} max={1.5} format={pct} onChange={(volume) => setStrip(t.id, { volume })} />
            <Slider label="Pan" value={t.mixer.pan} min={-1} max={1} format={panText} onChange={(pan) => setStrip(t.id, { pan })} />
            <Slider label="Reverb" value={t.mixer.sendReverb} min={0} max={1} format={pct} onChange={(sendReverb) => setStrip(t.id, { sendReverb })} />
            <Slider label="Delay" value={t.mixer.sendDelay} min={0} max={1} format={pct} onChange={(sendDelay) => setStrip(t.id, { sendDelay })} />
          </div>
        ))}
        <div className="strip fx" role="group" aria-label="Effects returns">
          <h3>Effects</h3>
          <Slider label="Reverb return" value={fx.reverb.returnLevel} min={0} max={1} format={pct} onChange={(returnLevel) => setFx("reverb", { returnLevel })} />
          <Slider label="Reverb decay" value={fx.reverb.decaySec} min={0.3} max={6} step={0.1} format={ms} onChange={(decaySec) => setFx("reverb", { decaySec })} />
          <Slider label="Delay return" value={fx.delay.returnLevel} min={0} max={1} format={pct} onChange={(returnLevel) => setFx("delay", { returnLevel })} />
          <Slider label="Delay time" value={fx.delay.timeSec} min={0.05} max={1.5} step={0.005} format={ms} onChange={(timeSec) => setFx("delay", { timeSec })} />
          <Slider label="Feedback" value={fx.delay.feedback} min={0} max={0.9} format={pct} onChange={(feedback) => setFx("delay", { feedback })} />
        </div>
        <div className="strip master" role="group" aria-label="Master and monitor">
          <h3>Master</h3>
          <LevelMeter label="Master level" value={meters.master} />
          <Slider label="Master" value={master.volume} min={0} max={1} format={pct} onChange={(volume) => controller.updateProject((p) => ({ ...p, master: { volume } }))} />
          <Slider label="Click level" value={metronome.volume} min={0} max={1} format={pct} onChange={(volume) => controller.updateProject((p) => ({ ...p, metronome: { ...p.metronome, volume } }))} hint="Metronome on the monitor bus" />
          <p className="hint">All buses currently sum to one stereo output. Separate monitor outputs depend on hardware validation (My Gear).</p>
        </div>
      </div>
    </Panel>
  );
}

/** Peak level on a −60…0 dBFS scale. Native <meter> gives assistive tech a value without extra ARIA. */
function LevelMeter({ label, value }: { label: string; value: number }) {
  const db = value <= 0 ? "silent" : `${Math.round(value * 60 - 60)} dBFS`;
  return <meter className="level" aria-label={label} title={`${label}: ${db}`} min={0} max={1} low={0.75} high={0.95} optimum={0.5} value={value} />;
}
